import React, { useCallback, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Cross2Icon } from "@radix-ui/react-icons";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Text } from "@radix-ui/themes";
import { toast } from "sonner";

import { useSimulator } from "../../context/SimulatorWrapper";
import { newReleasePolicyToInstructionJson } from "../../context/v1Helper";

import styles from "./styles.module.scss";
import { Release } from "./Release";

interface ReleaseDialogProps {
  isOpen: boolean;
  setOpen: (v: boolean) => void;
}

export const ReleaseDialog: React.FC<ReleaseDialogProps> = ({
  isOpen,
  setOpen,
}) => {
  const [isReleasing, setIsReleasing] = useState(false);
  const { publishRelease, currentRelease, topics } = useSimulator();

  const handleClose = useCallback(() => {
    if (isReleasing) {
      return;
    }
    setOpen(false);
  }, [setOpen, isReleasing]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (isReleasing) {
        return;
      }
      setOpen(open);
    },
    [setOpen, isReleasing]
  );

  const handleRelease = useCallback(async () => {
    if (!currentRelease || !topics) return;

    setIsReleasing(true);
    try {
      await publishRelease({
        releasePolicy: JSON.stringify(
          newReleasePolicyToInstructionJson(currentRelease, topics)
        ),
        filterPolicy:
          typeof currentRelease?.filterPolicy === "string"
            ? currentRelease?.filterPolicy
            : JSON.stringify(currentRelease?.filterPolicy || []),
        customToneOfVoice: currentRelease?.customToneOfVoice || "",
      });
      toast.success("Release published successfully");
      setOpen(false);
    } catch (error) {
      toast.error("Failed to publish release");
      console.error("Release error:", error);
    } finally {
      setIsReleasing(false);
    }
  }, [currentRelease, publishRelease, topics, setOpen]);

  return (
    <TooltipPrimitive.Provider delayDuration={0}>
      <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay
            className={styles.dialogOverlay}
            onClick={handleClose}
          />
          <Dialog.Content
            className={styles.dialogContent}
            onEscapeKeyDown={handleClose}
          >
            <div className={styles.topNav}>
              <Dialog.Close asChild>
                <button
                  className={styles.closeButton}
                  aria-label="Close"
                  onClick={handleClose}
                  disabled={isReleasing}
                >
                  <Cross2Icon />
                </button>
              </Dialog.Close>
              <h2 className={styles.navTitle}>Release Manager</h2>
            </div>
            <div className={styles.dialogBody}>
              <Release onRelease={handleRelease} isReleasing={isReleasing} />
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </TooltipPrimitive.Provider>
  );
};
